import React, {useState} from "react"
import {AsyncResultModal} from "../../../components/AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../../services/auth"
import {createAppConfig, patchAppConfig, AppConfig} from "../../../resources/whiteLabel"
import {usePaging} from "../../../hooks/usePaging"
import {findOrgsWithPaginationMeta, getOrgName, Org} from "../../../resources/org"
import {JWTProviders} from "../../../resources/orgSettings"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"

interface ManagedSolutionsConfigModalProps {
	mode: "create" | "edit"
	orgId?: string
	existingConfig?: AppConfig
	close: () => void
	onSuccess: () => void
}

type ManagedSolutionsConfigFormData = {
	jwtIssuer: string
	jwtProvider: JWTProviders
	jwksPath: string
	audience: string
	jwtPublicKey: string
	applicationFormVersion: string
	endUserConfigUrl: string
	findEndUsersUrl: string
	bankingPageURL: string
	isWireEnabled: boolean
	isQuickbooksEnabled: boolean
	isAppUsersEnabled: boolean
	isAccountCreationEnabled: boolean
}

export default function ManagedSolutionsConfigModal({
	mode,
	orgId,
	existingConfig,
	close,
	onSuccess,
}: ManagedSolutionsConfigModalProps) {
	const accessToken = useAccessToken()

	const [selectedOrgId, setSelectedOrgId] = useState<string>(orgId || "")

	const [formData, setFormData] = useState<ManagedSolutionsConfigFormData>({
		jwtIssuer: existingConfig?.attributes.jwtIssuer || "",
		jwtProvider: (() => {
			const provider = existingConfig?.attributes.jwtProvider?.provider
			// Check if provider is a valid enum value and not Custom
			if (
				provider &&
				Object.values(JWTProviders).includes(provider as JWTProviders) &&
				provider !== JWTProviders.Custom
			) {
				return provider as JWTProviders
			}
			return provider ? JWTProviders.Custom : JWTProviders.Custom
		})(),
		jwksPath: existingConfig?.attributes.jwtProvider?.jwksPath || "",
		audience: existingConfig?.attributes.jwtProvider?.audience || "",
		jwtPublicKey: existingConfig?.attributes.jwtPublicKey || "",
		applicationFormVersion: existingConfig?.attributes.applicationFormVersion || "v2",
		endUserConfigUrl: existingConfig?.attributes.endUserConfigUrl || "",
		findEndUsersUrl: existingConfig?.attributes.findEndUsersUrl || "",
		bankingPageURL: existingConfig?.attributes.bankingPageURL || "",
		isWireEnabled: existingConfig?.attributes.isWireEnabled || false,
		isQuickbooksEnabled: existingConfig?.attributes.isQuickbooksEnabled || false,
		isAppUsersEnabled: existingConfig?.attributes.isAppUsersEnabled || false,
		isAccountCreationEnabled: existingConfig?.attributes.isAccountCreationEnabled !== false,
	})

	// Fetch orgs for create mode
	const [orgsResult] = usePaging(
		10000, // Large limit to get all orgs
		(offset, limit) => findOrgsWithPaginationMeta(accessToken, offset, limit, false, "", [], undefined),
		(x) => x.data.length,
		[]
	)

	const [requestState, executeRequest] = useAsyncResultIdle(() => {
		const getJwtProvider = () => {
			if (formData.jwtProvider === JWTProviders.Custom) {
				return {
					provider: null,
					jwksPath: null,
					audience: null,
				}
			}
			return {
				provider: formData.jwtProvider,
				jwksPath: formData.jwksPath || null,
				audience: formData.audience || null,
			}
		}

		const attributes = {
			jwtIssuer: formData.jwtIssuer || undefined,
			jwtProvider: getJwtProvider(),
			jwtPublicKey: formData.jwtProvider === JWTProviders.Custom ? formData.jwtPublicKey || undefined : null,
			applicationFormVersion: formData.applicationFormVersion as "v1" | "v2",
			endUserConfigUrl: formData.endUserConfigUrl || undefined,
			findEndUsersUrl: formData.findEndUsersUrl || undefined,
			bankingPageURL: formData.bankingPageURL || undefined,
			isWireEnabled: formData.isWireEnabled || false,
			isQuickbooksEnabled: formData.isQuickbooksEnabled || false,
			isAppUsersEnabled: formData.isAppUsersEnabled || false,
			isAccountCreationEnabled: formData.isAccountCreationEnabled, // Default to true if not set
		}

		const targetOrgId = mode === "create" ? selectedOrgId : orgId

		if (!targetOrgId) {
			return Promise.reject(new Error("Org ID is required"))
		}

		if (mode === "create") {
			return createAppConfig(accessToken, {...attributes, jwtIssuer: formData.jwtIssuer}, targetOrgId)
		} else {
			return patchAppConfig(accessToken, attributes, targetOrgId)
		}
	}, [formData, mode, selectedOrgId, orgId, accessToken])

	const handleInputChange = (field: keyof ManagedSolutionsConfigFormData, value: string | boolean) => {
		setFormData((prev) => ({...prev, [field]: value}))
	}

	const isFormValid = mode === "create" ? !!formData.jwtIssuer && !!selectedOrgId : true

	return (
		<AsyncResultModal
			title={mode === "create" ? "Create App Configuration" : "Edit App Configuration"}
			buttonText={mode === "create" ? "Create Configuration" : "Save Changes"}
			buttonClassname="is-success"
			cancelButtonText="Cancel"
			cancelButtonClassname="is-white"
			state={requestState}
			onSubmit={executeRequest}
			onSuccess={onSuccess}
			successText={mode === "create" ? "Configuration created successfully!" : "Configuration updated successfully!"}
			errorToText={(err: any) => err.errors?.[0]?.title || "An error occurred"}
			close={close}
			buttonDisabled={!isFormValid}
		>
			{mode === "create" && (
				<div className="field">
					<label className="label">
						Org <span style={{color: "#ff3860"}}>*</span>
					</label>
					<div className="control">
						<div className="select is-fullwidth">
							<AsyncResultComponent asyncResult={orgsResult} pendingComponent={<div>Loading orgs...</div>}>
								{({value: orgs}) => {
									// Filter orgs to show those with relationships.parentOrg OR orgBankingSetup: ReadyToLaunchBanking tag
									const filteredOrgs = (orgs as {data: Org[]}).data.filter((org) => {
										const hasParentOrg = !!org.relationships?.parentOrg
										const hasManagedSolutionsTag = org.attributes.tags?.orgBankingSetup === "ReadyToLaunchBanking"
										return hasParentOrg || hasManagedSolutionsTag
									})

									return (
										<select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
											<option value="">Select an org...</option>
											{filteredOrgs.map((org) => (
												<option key={org.id} value={org.id}>
													{getOrgName(org)} (ID: {org.id})
												</option>
											))}
										</select>
									)
								}}
							</AsyncResultComponent>
						</div>
					</div>
					<p className="help">Select the org you want to create a configuration for.</p>
				</div>
			)}

			<div className="field">
				<label className="label">JWT Issuer {mode === "create" && <span style={{color: "#ff3860"}}>*</span>}</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Enter JWT issuer"
						value={formData.jwtIssuer}
						onChange={(e) => handleInputChange("jwtIssuer", e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">JWT Provider</label>
				<div className="control">
					<div className="select is-fullwidth">
						<select value={formData.jwtProvider} onChange={(e) => handleInputChange("jwtProvider", e.target.value)}>
							<option value={JWTProviders.Custom}>Custom</option>
							<option value={JWTProviders.Cognito}>Cognito</option>
							<option value={JWTProviders.Auth0}>Auth0</option>
							<option value={JWTProviders.Stytch}>Stytch</option>
							<option value={JWTProviders.Okta}>Okta</option>
							<option value={JWTProviders.CustomWithJWKS}>Custom with JWKS</option>
						</select>
					</div>
				</div>
			</div>

			{formData.jwtProvider !== JWTProviders.Custom && (
				<div className="field">
					<label className="label">JWKS Path</label>
					<div className="control">
						<input
							className="input"
							type="text"
							placeholder="Enter JWKS path"
							value={formData.jwksPath}
							onChange={(e) => handleInputChange("jwksPath", e.target.value)}
						/>
					</div>
				</div>
			)}

			{formData.jwtProvider === JWTProviders.CustomWithJWKS && (
				<div className="field">
					<label className="label">JWT Audience</label>
					<div className="control">
						<input
							className="input"
							type="text"
							placeholder="Enter JWT audience"
							value={formData.audience}
							onChange={(e) => handleInputChange("audience", e.target.value)}
						/>
					</div>
				</div>
			)}

			{formData.jwtProvider === JWTProviders.Custom && (
				<div className="field">
					<label className="label">JWT Public Key</label>
					<div className="control">
						<textarea
							className="textarea"
							placeholder="Enter JWT public key"
							rows={4}
							value={formData.jwtPublicKey}
							onChange={(e) => handleInputChange("jwtPublicKey", e.target.value)}
						/>
					</div>
				</div>
			)}

			<div className="field">
				<label className="label">Application Form Version</label>
				<div className="control">
					<div className="select is-fullwidth">
						<select
							value={formData.applicationFormVersion}
							onChange={(e) => handleInputChange("applicationFormVersion", e.target.value)}
						>
							<option value="v1">v1</option>
							<option value="v2">v2</option>
						</select>
					</div>
				</div>
			</div>

			<div className="field">
				<label className="label">End User Config URL</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Enter end user config URL"
						value={formData.endUserConfigUrl}
						onChange={(e) => handleInputChange("endUserConfigUrl", e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">Find End Users URL</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Enter find end users URL"
						value={formData.findEndUsersUrl}
						onChange={(e) => handleInputChange("findEndUsersUrl", e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">Banking Page URL</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Enter banking page URL"
						value={formData.bankingPageURL}
						onChange={(e) => handleInputChange("bankingPageURL", e.target.value)}
					/>
				</div>
			</div>

			<div className="feature-settings-container">
				<h3 className="feature-settings-title">Feature Settings</h3>

				<div className="field mb-3 feature-settings-grid">
					<label className="label feature-label">Wire Enabled</label>
					<div className="control">
						<input
							type="checkbox"
							className="feature-checkbox"
							checked={formData.isWireEnabled}
							onChange={(e) => handleInputChange("isWireEnabled", e.target.checked)}
						/>
					</div>

					<label className="label feature-label">Quickbooks Enabled</label>
					<div className="control">
						<input
							type="checkbox"
							className="feature-checkbox"
							checked={formData.isQuickbooksEnabled}
							onChange={(e) => handleInputChange("isQuickbooksEnabled", e.target.checked)}
						/>
					</div>

					<label className="label feature-label">App Users Enabled</label>
					<div className="control">
						<input
							type="checkbox"
							className="feature-checkbox"
							checked={formData.isAppUsersEnabled}
							onChange={(e) => handleInputChange("isAppUsersEnabled", e.target.checked)}
						/>
					</div>

					<label className="label feature-label">Account Creation Enabled</label>
					<div className="control">
						<input
							type="checkbox"
							className="feature-checkbox"
							checked={formData.isAccountCreationEnabled}
							onChange={(e) => handleInputChange("isAccountCreationEnabled", e.target.checked)}
						/>
					</div>
				</div>
			</div>
		</AsyncResultModal>
	)
}
